import imageUploadLogo from "../../assets/img/Upload-Photo-caregiver.svg";
import { ApiCaregiver } from "../../core/apis/caregiver";
import { AppRouteUi } from "../../config/appRoute";
import { Button } from "../../components/button.component";
import { Form, Toggle } from "rsuite";
import { useHistory } from "react-router";
import { useCallback, useEffect, useState } from "react";
import {
  DateInput,
  NumberInput,
  TagInputWithIcon,
} from "../../components/textInput.component";
import {
  DoesObjectHasAnyInvalidValue,
  generateMultipartFormData,
} from "../../utils/formUtils";
import {
  TextInput,
  FileInput,
  SelectPickerInput,
} from "../../components/textInput.component";
import { UploadedFileType } from "../../models/forms/uploadFileType";
import { CaregiverFormSchema } from "../../models/forms/caregiverForm";
import { UtilityDate } from "../../utils/dateUtils";
import { ActionsCaregiverTable } from "../../core/redux";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { StorageAuth } from "../../core/localStorage/StorageAuth";
import { Language } from "../../utils/language";
import { LocationSearchInput } from "../../components/locationSearchInput.component";
import { AddCertificateOnCaregiver } from "../../components/addCertificateOnCaregiver";

type CaregiverFormData = {
  DateOfBirth: Date;
  FirstName: string;
  UserName: string;
  Gender: string;
  LastName: string;
  PhoneNumber: string;
  ProfilePic: UploadedFileType[];
  Rate: string;
  email: string;
  SSN: string;
  ChampsIdNo: string;
  Certificates: CertificateInterface[];
  Address: string;
  Languages: string[];
  AvailableFrom: Date;
  AvailableTo: Date;
  Password: string;
  WeeklyAvailabilities: string[];
  ApartmentNumber: string;
};

export interface CertificateInterface {
  certificateFile?: UploadedFileType[];
  expiationDate?: Date;
}

export const AddCaregiver = ({
  showAvailableDays,
}: {
  showAvailableDays: boolean;
}) => {
  const routeHistory = useHistory<{ from?: string }>();
  const [autoClockCheckIn, setAutoClockCheckIn] = useState(false);
  const [formError, setFormError] = useState({});
  const [certificates, setCertificates] = useState<CertificateInterface[]>([]);

  const genders = ["Male", "Female", "Others"].map((gender) => ({
    label: gender,
    value: gender,
  }));

  const availableDays = [
    "Saturday",
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
  ].map((day) => ({
    label: day,
    value: day,
  }));

  const [formData, setFormData] = useState<CaregiverFormData>({
    DateOfBirth: new Date(),
    UserName: "",
    FirstName: "",
    Gender: "",
    LastName: "",
    PhoneNumber: "",
    ProfilePic: [],
    Rate: "",
    email: "",
    SSN: "",
    ChampsIdNo: "",
    Certificates: [],
    Address: "",
    Languages: ["English"],
    AvailableFrom: new Date(),
    AvailableTo: new Date(),
    Password: "",
    WeeklyAvailabilities: [],
    ApartmentNumber: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isTransportAvailable, setIsTransportAvailable] = useState(false);
  const [isGPSCheckedIn, setIsGPSCheckedIn] = useState(false);
  const [isNotificationOn, setIsNotificationOn] = useState(false);
  const [isAddressTouched, setIsAddressTouched] = useState(false);
  const [isAvailableForWholeDay, setIsAvailableForWholeDay] = useState(false);
  const { CompanyId } = StorageAuth;

  const dispatch = useAppDispatch();

  useEffect(() => {
    const { ApartmentNumber, ...rest } = formData;
    const isEmpty = Object.values({ ...rest }).some(
      DoesObjectHasAnyInvalidValue
    );

    const errorCount = Object.keys(formError).length;
    const requiredFilled =
      !!formData.AvailableFrom &&
      !!formData.AvailableTo &&
      !!formData.DateOfBirth;
    var isDisabled =
      isEmpty ||
      !!errorCount ||
      !requiredFilled ||
      (showAvailableDays &&
        (!formData.WeeklyAvailabilities ||
          formData.WeeklyAvailabilities.length === 0));
    setIsDisabled(isDisabled);
  }, [formData, formError, showAvailableDays, certificates]);

  const handleChange = useCallback(
    (data: any, _: any) =>
      setFormData((prevFormData) => ({ ...prevFormData, ...data })),
    []
  );

  const setAvailableForWholeDay = () => {
    setIsAvailableForWholeDay(!isAvailableForWholeDay);

    if (!isAvailableForWholeDay) {
      const date = new Date();
      formData.AvailableFrom = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0,
        0,
        0
      );
      formData.AvailableTo = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59
      );
    } else {
      formData.AvailableFrom = new Date();
      formData.AvailableTo = new Date();
    }
  };

  const convertToPhoneNumberFormat = (
    phoneNumber: string,
    propertyName: string
  ) => {
    phoneNumber = phoneNumber.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength > 3 && phoneNumberLength < 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumberLength >= 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6, 10)}`;
    }

    setFormData((prev) => ({ ...prev, [propertyName]: phoneNumber }));
  };

  const convertToSSNFormat = (ssn: string) => {
    ssn = ssn.replace(/[^\d]/g, "");
    const ssnLength = ssn.length;

    if (ssnLength > 3 && ssnLength < 6) {
      ssn = `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
    } else if (ssnLength >= 6) {
      ssn = `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
    }

    setFormData((prev) => ({ ...prev, SSN: ssn }));
  };

  const handleSubmit = async (
    checked: boolean,
    event: React.FormEvent<HTMLFormElement>
  ) => {
    if (!checked) return;
    setIsLoading(true);
    formData.SSN = formData.SSN.replace(/[-]/g, "");
    const multipartFormData = generateMultipartFormData(formData);

    certificates.forEach((cert, index) => {
      if (cert.certificateFile && cert.expiationDate) {
        multipartFormData.append(
          `Certificates[${index}].CertificateFile`,
          cert.certificateFile[0].blobFile
        );
        multipartFormData.append(
          `Certificates[${index}].ExpirationTime`,
          cert.expiationDate.toISOString()
        );
      }
    });

    if (autoClockCheckIn) {
      multipartFormData.append("AutoClockCheckIn", "true");
    } else multipartFormData.append("AutoClockCheckIn", "false");

    multipartFormData.append(
      "IsTransportAvailable",
      isTransportAvailable ? "true" : "false"
    );
    multipartFormData.append(
      "GpsCheckInTurnedOn",
      isGPSCheckedIn ? "true" : "false"
    );
    multipartFormData.append("IsNotificationEnabled", `${isNotificationOn}`);
    multipartFormData.append("CompanyId", CompanyId || "");

    const result = await ApiCaregiver.Add(multipartFormData);

    if (result.isSuccess) {
      dispatch(ActionsCaregiverTable.goToPage(1));
      routeHistory.length > 1
        ? routeHistory.goBack()
        : routeHistory.push(AppRouteUi.Root);
    }

    setIsLoading(false);
  };

  const getTimeChangeHandler = (name: string) => {
    return (time: Date) => {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: time,
      }));
    };
  };

  return (
    <Form
      className="row"
      onCheck={setFormError}
      onChange={handleChange}
      onSubmit={handleSubmit}
      model={CaregiverFormSchema}
      fluid
    >
      <div className="col-md-10 col-12 border-end px-4 add-page-ctl">
        <div className="row">
          <TextInput name="FirstName" required>
            First Name
          </TextInput>
          <TextInput name="LastName" required>
            Last Name
          </TextInput>
        </div>

        <div className="row">
          <TextInput name="UserName" required>
            User Name
          </TextInput>
          <div className="col-md-6 col-12 px-1 rs-form-group" role="group">
            <label className="rs-form-control-label">
              Password :<span className="text-red-300">*</span>
            </label>
            <div className="rs-form-control rs-form-control-wrapper">
              <input
                name="Password"
                className="rs-input"
                type="password"
                autoComplete="new-password"
                value={formData.Password}
                onChange={(event) => {
                  setFormData({ ...formData, Password: event.target.value });
                }}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <SelectPickerInput
            data={genders}
            name="Gender"
            required
            searchable={false}
            renderEmpty={<p className="text-muted p-3">No Data Available</p>}
          >
            Gender
          </SelectPickerInput>
          <DateInput
            ranges={[]}
            disabledDate={UtilityDate.isDateAfterToday}
            name="DateOfBirth"
            required
          >
            Date of birth
          </DateInput>
        </div>

        <div className="row">
          <TextInput
            name="PhoneNumber"
            required
            value={formData.PhoneNumber}
            onChange={(val) => convertToPhoneNumberFormat(val, "PhoneNumber")}
          >
            Phone Number
          </TextInput>
          <TextInput name="email" required>
            Email
          </TextInput>
        </div>

        <div className="row">
          <TextInput
            name="SSN"
            required
            value={formData.SSN}
            onChange={(val) => convertToSSNFormat(val)}
          >
            Social security number
          </TextInput>
          <NumberInput name="ChampsIdNo" required>
            Champ's id number
          </NumberInput>
        </div>

        <div className="row">
          <NumberInput name="Rate" required>
            Caregiver's rate
          </NumberInput>
          {showAvailableDays && (
            <TagInputWithIcon
              name="WeeklyAvailabilities"
              data={availableDays}
              required
              value={formData.WeeklyAvailabilities}
              placeholder="Select Available Days"
              renderEmpty={<p className="text-muted p-3">No Data Available</p>}
            >
              Available Days
            </TagInputWithIcon>
          )}
        </div>

        <div className="row">
          <Form.Group className="col-md-6 px-1">
            <Form.ControlLabel>
              Address <span className="text-red-300">*</span>
            </Form.ControlLabel>
            <LocationSearchInput
              onSelect={(address) => {
                setFormData({ ...formData, Address: address });
              }}
              onBlur={() => setIsAddressTouched(true)}
              value={formData.Address}
            />
            {isAddressTouched && !formData.Address && (
              <small className="text-red-300 mb-2">
                Please select a valid address
              </small>
            )}
          </Form.Group>
          <TextInput name="ApartmentNumber">Apartment</TextInput>
        </div>
        <div className="row">
          <div className="col-md-3 align-items-center px-1 mb-3">
            Available for 24 hours
            <Toggle
              onChange={() => setAvailableForWholeDay()}
              checked={isAvailableForWholeDay}
              size="sm"
              className="ms-2"
            />
          </div>
        </div>

        <div className="row">
          <DateInput
            name="AvailableFrom"
            value={formData.AvailableFrom}
            time
            timeChange={getTimeChangeHandler("AvailableFrom")}
            required
            disabled={isAvailableForWholeDay}
          >
            Available From
          </DateInput>
          <DateInput
            name="AvailableTo"
            value={formData.AvailableTo}
            time
            timeChange={getTimeChangeHandler("AvailableTo")}
            required
            disabled={isAvailableForWholeDay}
            disabledHours={(hour) => hour < formData.AvailableFrom.getHours()}
          >
            Available To
          </DateInput>
        </div>

        <div className="row">
          <TagInputWithIcon
            name="Languages"
            data={Language.getLanguageSelectItems()}
            required
            value={formData.Languages}
            renderEmpty={<p className="text-muted p-3">No Data Available</p>}
          >
            Languages
          </TagInputWithIcon>
        </div>

        <div className="row">
          <div className="align-items-center px-1 mb-3">
            Transportation Availability
            <Toggle
              onChange={() => setIsTransportAvailable(!isTransportAvailable)}
              checked={isTransportAvailable}
              size="sm"
              className="ms-2"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 align-items-center px-1 mb-3">
            GPS
            <Toggle
              onChange={() => setIsGPSCheckedIn(!isGPSCheckedIn)}
              checked={isGPSCheckedIn}
              size="sm"
              className="ms-2"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 align-items-center px-1 mb-3">
            Auto Clock
            <Toggle
              onChange={() => setAutoClockCheckIn(!autoClockCheckIn)}
              checked={autoClockCheckIn}
              size="sm"
              className="ms-2"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 align-items-center px-1 mb-3">
            Notification
            <Toggle
              onChange={() => setIsNotificationOn(!isNotificationOn)}
              checked={isNotificationOn}
              size="sm"
              className="ms-2"
            />
          </div>
        </div>

        <div className="row">
          <FileInput
            listType="picture"
            imageSrc={imageUploadLogo}
            name="ProfilePic"
            accept="image/*"
          >
            Upload Photo
          </FileInput>

          <div className="row px-1">
            <AddCertificateOnCaregiver
              onChange={setCertificates}
              value={certificates}
            />
          </div>
        </div>
      </div>

      <div className="col-md-2 col-12">
        <Button
          className="m-1 w-100 btn-green"
          type="submit"
          name="Save"
          loading={isLoading}
          disabled={isDisabled}
        />

        <Button
          onClick={() =>
            routeHistory.push(
              routeHistory.location.state?.from || AppRouteUi.Caregiver.List()
            )
          }
          className="m-1 w-100 btn-green-subtle"
          disabled={isLoading}
          name="Return without saving"
        />

        {Object.keys(formError).length > 0 && (
          <div className="form-errors">
            {Object.entries(formError).map(([field, error]) => (
              <div key={field} className="error-message">
                {field}: {error}
              </div>
            ))}
          </div>
        )}
      </div>
    </Form>
  );
};
